import { useProducts, type Product } from "@/lib/db";
import { useCart } from "@/stores/cartStore";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

const UNIT_LABEL: Record<string, string> = {
  stueck: "Stück", glas: "Glas", flasche: "Flasche", beutel: "Beutel", kg: "kg", liter: "Liter",
};

export default function KatalogPage() {
  const { data: products = [], isLoading } = useProducts({ onlyActive: true });

  // Gruppiere nach Kategorie
  const byCat = new Map<string, Product[]>();
  for (const p of products) {
    const cat = p.category ?? "Hof-Produkte";
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat)!.push(p);
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-brand font-bold text-wappen-700">Willkommen im Hofladen</h1>
        <p className="text-hof-700 mt-1">Frische Suppen, Eier und Hofprodukte — täglich Mo bis Sa zu Ihnen nach Hause.</p>
      </header>

      {isLoading ? (
        <div className="card">Lade Produkte…</div>
      ) : products.length === 0 ? (
        <div className="card text-center text-hof-600">
          Aktuell sind keine Produkte verfügbar. Schauen Sie später wieder vorbei!
        </div>
      ) : (
        Array.from(byCat.entries()).map(([cat, items]) => (
          <section key={cat} className="space-y-3">
            <h2 className="text-xl font-semibold text-hof-800">{cat}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function ProductCard({ p }: { p: Product }) {
  const add = useCart((s) => s.addItem);
  const items = useCart((s) => s.items);
  const inCart = items.find((i) => i.product_id === p.id);

  return (
    <article className="card flex flex-col">
      {p.image_url ? (
        <img src={p.image_url} alt={p.name} className="aspect-video object-cover rounded-xl mb-3" />
      ) : (
        <div className="aspect-video bg-wiese-100 rounded-xl mb-3 flex items-center justify-center text-wiese-400 text-4xl">
          🥕
        </div>
      )}
      <h3 className="font-semibold text-hof-900">{p.name}</h3>
      {p.description && <p className="text-sm text-hof-600 mt-1 line-clamp-2">{p.description}</p>}
      <div className="flex items-end justify-between mt-3 pt-3 border-t border-hof-100">
        <div>
          <div className="text-2xl font-bold text-wiese-700">{fmt.format(p.price_cents / 100)}</div>
          <div className="text-xs text-hof-500">je {UNIT_LABEL[p.unit] ?? p.unit}</div>
        </div>
        <button
          onClick={() => add({
            product_id: p.id, qty: 1, unit_price_cents: p.price_cents,
            name: p.name, unit: UNIT_LABEL[p.unit] ?? p.unit,
          })}
          className="btn-primary"
        >
          {inCart ? `+1 (${inCart.qty})` : "In den Korb"}
        </button>
      </div>
    </article>
  );
}
